import React, { useContext } from 'react';
import HeroSection from '../components/HeroSection';
import PricingCard from '../components/PricingCard';
import TestimonialCard from '../components/TestimonialCard';
import { CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ModalContext } from '../App';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import '../components/ServicePageLayout.css';

const TrademarkRegistration = () => {
  const { openModal } = useContext(ModalContext);
  const [openFaq, setOpenFaq] = React.useState(null);

  const heroData = {
    eyebrow: 'TRADEMARK REGISTRATION',
    title: 'Protect Your Brand Identity',
    description: 'Secure exclusive rights to your brand name, logo, and identity with professional trademark registration services starting at just $45.',
    backgroundImage: 'https://images.unsplash.com/photo-1564846824194-346b7871b855',
    showForm: false,
    ctaText: 'Start Registration',
    ctaLink: '#pricing'
  };

  const processSteps = [
    {
      title: 'Information Collection',
      description: 'We begin by gathering comprehensive details about your brand through a detailed consultation. Our trademark specialists review your business information, intended use of the mark, and specific goods or services to ensure we have everything needed for a strong application.'
    },
    {
      title: 'Research and Search Report',
      description: 'Our expert team conducts an exhaustive trademark search across federal databases, state registrations, and common law usage. We analyze potential conflicts and provide you with a detailed report outlining any risks, ensuring your mark has the best chance of approval.'
    },
    {
      title: 'Application and Submission',
      description: 'We prepare your complete trademark application with accurate classifications, compelling descriptions, and proper documentation. Our team files directly with the USPTO and monitors your application throughout the examination process, handling any office actions promptly.'
    }
  ];

  const pricingPlans = [
    { 
      name: 'Basic', 
      price: '45', 
      features: [
        'Case Review',
        'Case Filing',
        'Basic Search',
        'State E-Filing',
        'Trademark Secured'
      ] 
    },
    { 
      name: 'Deluxe', 
      price: '145', 
      features: [
        'Case Review',
        'Case Filing',
        'Basic Search',
        'State E-Filing',
        'Trademark Secured',
        'Direct Hit Search',
        'Dedicated Case Manager'
      ], 
      isPopular: true 
    },
    { 
      name: 'Premium', 
      price: '299', 
      features: [
        'Case Review (by a TM Expert)',
        'Basic Free Trademark Search',
        'Trademark Monitoring Services (3 Months)',
        'Trademark Secured',
        'Refusal Risk Meter',
        'Case filing service with complete paperwork'
      ] 
    },
    { 
      name: 'Elite', 
      price: '599', 
      features: [
        'Case Review & Filing (U.S Registered IP Attorney)',
        'Assigned Case Filing Officer',
        'Private Registration',
        'Trademark Secured',
        'Federal Search & Clearance Report (All States)',
        'Federal E-Filing (USPTO)',
        'Cease & Desist Letter',
        'Same Day Filing',
        'Trademark Monitoring (1 Year)',
        'Direct Hit Search',
        'Dedicated Case Manager',
        '24/7 Secure Online Access',
        '100% Approval Guarantee'
      ] 
    }
  ];

  const whyChooseData = {
    paragraphs: [
      'Protecting your trademark is one of the most important investments you can make in your business. At Unity Trademark, we combine legal expertise with personalized service to ensure your brand receives comprehensive protection. Our team of experienced trademark specialists and registered attorneys has successfully filed thousands of trademark applications across virtually every industry.',
      'We understand that navigating the trademark registration process can be overwhelming. That is why we have streamlined our services to make brand protection accessible and affordable for businesses of all sizes. From small startups to established corporations, we provide the same level of professional attention and dedication to every client, ensuring your trademark application is prepared with precision and filed correctly the first time.'
    ],
    features: [
      {
        title: 'Expert Legal Guidance',
        description: 'Our registered attorneys and trademark specialists bring decades of combined experience in intellectual property law. Every application is carefully reviewed to maximize your chances of approval and ensure proper protection for your brand.'
      },
      {
        title: 'Comprehensive Search Services',
        description: 'We conduct thorough searches across federal registrations, state databases, and common law usage to identify potential conflicts before filing. Our detailed reports help you make informed decisions about your trademark strategy.'
      },
      {
        title: 'Transparent Fixed Pricing',
        description: 'No hidden fees or surprise charges. Our straightforward pricing includes everything you need for successful trademark registration, from initial consultation to final certificate delivery.'
      },
      {
        title: 'Dedicated Support Team',
        description: 'From your first inquiry to post-registration support, our dedicated case managers are available to answer questions, provide updates, and guide you through every step of the trademark registration process.'
      }
    ]
  };

  const testimonials = [
    { name: 'Jennifer Martinez', company: 'Tech Startup Founder', text: 'The team at Unity Trademark made trademark registration incredibly simple. They handled everything professionally and kept me informed throughout the entire process. Highly recommend their services!', image: 'https://i.pravatar.cc/150?img=47' },
    { name: 'Robert Chen', company: 'E-commerce Business Owner', text: 'I was worried about potential conflicts with my brand name, but their comprehensive search gave me confidence. The whole process was smooth, and they got my trademark registered without any issues.', image: 'https://i.pravatar.cc/150?img=12' },
    { name: 'Sarah Thompson', company: 'Restaurant Owner', text: 'Affordable pricing and exceptional service. They explained everything in terms I could understand and made sure my restaurant name was properly protected. Worth every penny!', image: 'https://i.pravatar.cc/150?img=5' },
    { name: 'Michael Rodriguez', company: 'Fashion Designer', text: 'As a small business owner, I appreciated their transparent pricing and no-nonsense approach. They delivered exactly what they promised and my trademark is now registered.', image: 'https://i.pravatar.cc/150?img=13' },
    { name: 'Emily Watson', company: 'Marketing Agency Owner', text: 'Professional, efficient, and knowledgeable. They handled a complex office action for us and got our trademark approved. Their expertise saved us time and money.', image: 'https://i.pravatar.cc/150?img=9' }
  ];

  const faqs = [
    {
      question: 'How long does the trademark registration process take?',
      answer: 'The complete trademark registration process typically takes 8-12 months from filing to final registration. This timeline includes USPTO processing time, publication period, and potential office actions. While we file your application promptly, the overall duration depends on USPTO workload and whether any issues arise during examination.'
    },
    {
      question: 'What is the difference between state and federal trademark registration?',
      answer: 'Federal trademark registration with the USPTO provides nationwide protection and legal presumptions of ownership and validity. State registration only protects your mark within that specific state. We recommend federal registration for most businesses as it offers broader protection and stronger legal rights.'
    },
    {
      question: 'Can I trademark a name that is similar to an existing trademark?',
      answer: 'It depends on several factors including the similarity of the marks, relatedness of goods or services, and likelihood of consumer confusion. Our comprehensive trademark search helps identify potential conflicts, and our attorneys can advise whether your mark is distinctive enough for registration.'
    },
    {
      question: 'What happens if my trademark application is refused?',
      answer: 'If the USPTO issues an office action or refusal, we review the reasons and prepare a comprehensive legal response. Many refusals can be overcome with proper arguments and evidence. Our premium packages include office action responses to help navigate any challenges that arise.'
    },
    {
      question: 'Do I need to use my trademark before filing an application?',
      answer: 'You can file based on either actual use in commerce or intent to use. If filing based on intent to use, you must submit proof of use before the trademark will be registered. Our team will guide you on the best filing basis for your situation and help prepare the necessary documentation.'
    }
  ];

  return (
    <div className="service-page">
      <HeroSection {...heroData} />

      <section className="section">
        <div className="container">
          <h2 className="section-title">About Trademark Registration</h2>
          <p className="section-subtitle">
            Trademark registration is a critical legal process that establishes your exclusive rights to a brand name, logo, slogan, or other identifying mark. By registering with the United States Patent and Trademark Office (USPTO), you gain nationwide protection, the legal presumption of ownership, and the ability to prevent others from using confusingly similar marks in connection with related goods or services. A registered trademark becomes a valuable business asset that strengthens your market position and provides a foundation for brand growth and recognition.
          </p>
        </div>
      </section>

      <section className="section section-light">
        <div className="container">
          <h2 className="section-title">How We Work</h2>
          <p className="section-subtitle">
            Our streamlined three-step process makes trademark registration simple and stress-free. From initial consultation to final certificate delivery, we handle every aspect of your application with professional care and attention to detail.
          </p>
          <div className="process-grid">
            {processSteps.map((step, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{String(index + 1).padStart(2, '0')}</div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="section">
        <div className="container">
          <h2 className="section-title">Choose Your Package</h2>
          <p className="section-subtitle">
            Select the trademark registration package that best fits your needs and budget. All packages include professional service, expert guidance, and comprehensive support throughout the registration process.
          </p>
          <div className="pricing-grid">
            {pricingPlans.map((plan, index) => (
              <PricingCard 
                key={index} 
                {...plan} 
                onGetStarted={(packageName) => openModal(`Trademark Registration - ${packageName}`)}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="section section-light">
        <div className="container">
          <h2 className="section-title">Why Choose Unity Trademark</h2>
          <div className="why-choose-content">
            {whyChooseData.paragraphs.map((paragraph, index) => (
              <p key={index} className="why-choose-paragraph">{paragraph}</p>
            ))}
          </div>
          <div className="features-grid">
            {whyChooseData.features.map((feature, index) => (
              <div key={index} className="feature-card">
                <CheckCircle size={40} className="feature-icon-svg" />
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Protect Your Brand?</h2>
            <p className="cta-description">
              Get started with professional trademark registration for just $45. Join thousands of satisfied clients who trust Unity Trademark for comprehensive brand protection and expert legal guidance.
            </p>
            <Button 
              size="lg" 
              className="cta-button"
              onClick={() => openModal('Trademark Registration')}
            >
              Start Your $45 Trademark Registration
            </Button>
          </div>
        </div>
      </section>

      <section className="section section-light">
        <div className="container">
          <h2 className="section-title">Client Success Stories</h2>
          <div className="testimonials-swiper-container">
            <Swiper
              modules={[Autoplay, Navigation, Pagination]}
              spaceBetween={30}
              slidesPerView={1}
              navigation
              pagination={{ clickable: true }}
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
              }}
              loop={true}
              breakpoints={{
                640: {
                  slidesPerView: 1,
                  spaceBetween: 20,
                },
                768: {
                  slidesPerView: 2,
                  spaceBetween: 25,
                },
                1024: {
                  slidesPerView: 3,
                  spaceBetween: 30,
                },
              }}
              className="testimonials-swiper"
            >
              {testimonials.map((testimonial, index) => (
                <SwiperSlide key={index}>
                  <TestimonialCard {...testimonial} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-container">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <button
                  className="faq-question"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span>{faq.question}</span>
                  <span className="faq-icon">{openFaq === index ? '−' : '+'}</span>
                </button>
                {openFaq === index && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrademarkRegistration;